/* ============================================================================
   GLORY PREDICTIVE SEARCH — LOGIC
   
   Hooks na .glory-search-input u glory-search-overlay.
   - Debounced fetch (220ms) → /search/suggest.json
   - Filtrira osiguranje-posiljke iz rezultata
   - Real-time highlight matched delova
   - Keyboard navigation (↑↓ Enter Esc)
   - Mobile-aware (auto repositioning ispod overlay-a)
   - Pamti dropdown poziciju kad se overlay otvori/zatvori
   
   Ne dira Dawn predictive-search snippet — paralelan sistem.
   ============================================================================ */

(function () {
  'use strict';

  const DEBOUNCE_MS = 220;
  const DESKTOP_LIMIT = 6;
  const MOBILE_LIMIT = 4;
  const MOBILE_BREAKPOINT = 749;
  const MIN_QUERY_LENGTH = 2;
  const INSURANCE_HANDLE = 'osiguranje-posiljke';

  let dropdown = null;
  let inner = null;
  let searchInput = null;
  let searchOverlay = null;
  let searchForm = null;
  let itemTemplate = null;
  let listEl = null;
  let seeAllEl = null;
  let countEl = null;
  let emptyTermEl = null;

  let debounceTimer = null;
  let activeFetch = null;
  let activeIndex = -1;
  let currentItems = [];
  let lastQuery = '';
  let isInitialized = false;
  let keyboardBlurTimer = null;
  const KEYBOARD_BLUR_MS = 3500;

  // ============== INIT ==============
  document.addEventListener('DOMContentLoaded', init);

  function init() {
    if (isInitialized) return;

    dropdown = document.getElementById('GloryPredictiveDropdown');
    searchInput = document.querySelector('.glory-search-input');
    searchOverlay = document.querySelector('.glory-search-overlay');
    searchForm = document.querySelector('.glory-search-form');
    itemTemplate = document.getElementById('GloryPredictiveItemTemplate');

    if (!dropdown || !searchInput || !searchOverlay) {
      return; // tihi fail — search elementi ne postoje (možda druga stranica)
    }

    inner = dropdown.querySelector('.glory-predictive__inner');
    listEl = dropdown.querySelector('[data-glory-predictive-list]');
    seeAllEl = dropdown.querySelector('[data-glory-predictive-see-all]');
    countEl = dropdown.querySelector('[data-glory-predictive-count]');
    emptyTermEl = dropdown.querySelector('[data-glory-predictive-empty-term]');

    // Postavi role/ARIA na input
    searchInput.setAttribute('role', 'combobox');
    searchInput.setAttribute('aria-autocomplete', 'list');
    searchInput.setAttribute('aria-expanded', 'false');
    searchInput.setAttribute('aria-controls', 'GloryPredictiveDropdown');

    // Hook events
    searchInput.addEventListener('input', handleInput);
    searchInput.addEventListener('keydown', handleKeydown);
    searchInput.addEventListener('focus', handleFocus);

    // Reposition on resize / scroll
    window.addEventListener('resize', positionDropdown);

    // Watch da se zatvori dropdown kad se zatvori overlay
    observeOverlayState();

    isInitialized = true;
  }

  // ============== INPUT HANDLER ==============
  function handleInput(e) {
    const value = e.target.value.trim();

    if (debounceTimer) clearTimeout(debounceTimer);
    // Reset timer-a za auto-blur tastature na svako kucanje
    cancelKeyboardBlurTimer();

    if (value.length < MIN_QUERY_LENGTH) {
      hideDropdown();
      return;
    }

    if (value === lastQuery) return; // nema promene
    lastQuery = value;

    showState('loading');
    showDropdown();

    debounceTimer = setTimeout(function () {
      fetchSuggestions(value);
    }, DEBOUNCE_MS);
  }

  // ============== FETCH ==============
  function fetchSuggestions(query) {
    // Cancel prethodni request ako traje
    if (activeFetch && typeof activeFetch.abort === 'function') {
      try { activeFetch.abort(); } catch (e) {}
    }

    const displayLimit = isMobile() ? MOBILE_LIMIT : DESKTOP_LIMIT;
    // Uvek fetchujemo MAX (10) da bi count bio tačan na svim ekranima.
    // Display limit primenjujemo posle filtriranja.
    const fetchLimit = 10;

    const url = '/search/suggest.json?q=' + encodeURIComponent(query) +
                '&resources[type]=product' +
                '&resources[limit]=' + fetchLimit +
                '&resources[options][unavailable_products]=last' +
                '&resources[options][fields]=title,product_type,variants.title,vendor';

    const controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    activeFetch = controller;

    fetch(url, {
      headers: { 'Accept': 'application/json' },
      signal: controller ? controller.signal : undefined
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Search failed');
        return r.json();
      })
      .then(function (data) {
        // Zaštita od race conditiona — query se možda promenio
        if (lastQuery !== query) return;

        const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        const filtered = products.filter(function (p) {
          return p.handle !== INSURANCE_HANDLE;
        });

        const sliced = filtered.slice(0, displayLimit);
        currentItems = sliced;

        if (sliced.length === 0) {
          renderEmpty(query);
        } else {
          renderResults(sliced, query, filtered.length);
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return; // tihi cancel
        console.warn('[Glory Predictive] Fetch error:', err);
        renderEmpty(query);
      });
  }

  // ============== RENDER — RESULTS ==============
  function renderResults(items, query, totalCount) {
    if (!listEl || !itemTemplate) return;

    // Clean prethodne rezultate
    listEl.innerHTML = '';
    activeIndex = -1;

    items.forEach(function (product, index) {
      const node = itemTemplate.content.firstElementChild.cloneNode(true);
      const link = node.querySelector('[data-glory-predictive-link]');
      const img = node.querySelector('[data-glory-predictive-image]');
      const brand = node.querySelector('[data-glory-predictive-brand]');
      const name = node.querySelector('[data-glory-predictive-name]');
      const price = node.querySelector('[data-glory-predictive-price]');

      // URL
      if (link) {
        link.href = product.url;
        link.setAttribute('data-index', index);
      }

      // Slika
      if (img) {
        if (product.featured_image && product.featured_image.url) {
          img.src = appendImageSize(product.featured_image.url, 120);
          img.alt = product.title || '';
        } else if (product.image) {
          img.src = appendImageSize(product.image, 120);
          img.alt = product.title || '';
        } else {
          img.style.display = 'none';
        }
      }

      // Brand (vendor)
      if (brand) {
        if (product.vendor) {
          brand.textContent = product.vendor;
        } else {
          brand.style.display = 'none';
        }
      }

      // Naziv (sa highlight-om)
      if (name) {
        name.innerHTML = highlightMatch(product.title || '', query);
      }

      // Cena
      if (price) {
        price.textContent = formatMoney(product.price, product.price_min);
      }

      listEl.appendChild(node);
    });

    // See all link i count
    if (seeAllEl) {
      seeAllEl.href = '/search?q=' + encodeURIComponent(query) +
                      '&options%5Bprefix%5D=last&type=product';
    }
    if (countEl) {
      countEl.textContent = totalCount;
    }

    showState('results');

    // Mobile: posle 3.5s neaktivnosti, automatski zatvori tastaturu
    // da kupac vidi ceo dropdown
    scheduleKeyboardBlur();
  }

  // ============== RENDER — EMPTY ==============
  function renderEmpty(query) {
    if (emptyTermEl) {
      emptyTermEl.textContent = '„' + query + '"';
    }
    showState('empty');
    scheduleKeyboardBlur();
  }

  // ============== KEYBOARD AUTO-BLUR (mobile UX) ==============
  function scheduleKeyboardBlur() {
    if (!isMobile()) return; // samo na mobilnom
    cancelKeyboardBlurTimer();
    keyboardBlurTimer = setTimeout(function () {
      if (searchInput && document.activeElement === searchInput) {
        searchInput.blur();
      }
    }, KEYBOARD_BLUR_MS);
  }

  function cancelKeyboardBlurTimer() {
    if (keyboardBlurTimer) {
      clearTimeout(keyboardBlurTimer);
      keyboardBlurTimer = null;
    }
  }

  // ============== STATE TOGGLE ==============
  function showState(state) {
    const states = dropdown.querySelectorAll('[data-glory-predictive-state]');
    states.forEach(function (el) {
      el.hidden = (el.getAttribute('data-glory-predictive-state') !== state);
    });
  }

  // ============== DROPDOWN OPEN/CLOSE ==============
  function showDropdown() {
    if (!dropdown) return;
    dropdown.hidden = false;
    positionDropdown();

    // Force reflow pa transition radi
    void dropdown.offsetWidth;

    requestAnimationFrame(function () {
      dropdown.classList.add('is-open');
      dropdown.setAttribute('aria-hidden', 'false');
      if (searchInput) searchInput.setAttribute('aria-expanded', 'true');
    });
  }

  function hideDropdown() {
    if (!dropdown) return;
    dropdown.classList.remove('is-open');
    dropdown.setAttribute('aria-hidden', 'true');
    if (searchInput) searchInput.setAttribute('aria-expanded', 'false');

    setTimeout(function () {
      if (!dropdown.classList.contains('is-open')) {
        dropdown.hidden = true;
      }
    }, 280);

    activeIndex = -1;
    if (debounceTimer) clearTimeout(debounceTimer);
  }

  // ============== POSITIONING ==============
  function positionDropdown() {
    if (!dropdown || !searchOverlay) return;
    const rect = searchOverlay.getBoundingClientRect();
    // dropdown ide ispod overlay-a — top = rect.bottom
    dropdown.style.top = Math.max(0, rect.bottom) + 'px';
  }

  // ============== KEYBOARD NAV ==============
  function handleKeydown(e) {
    if (!dropdown.classList.contains('is-open')) return;

    const items = listEl ? listEl.querySelectorAll('.glory-predictive__item') : [];

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveActive(items, 1);
        break;

      case 'ArrowUp':
        e.preventDefault();
        moveActive(items, -1);
        break;

      case 'Enter':
        // Ako je item selektovan strelicama → otvori taj
        if (activeIndex >= 0 && items[activeIndex]) {
          e.preventDefault();
          const link = items[activeIndex].querySelector('[data-glory-predictive-link]');
          if (link && link.href) window.location.href = link.href;
        }
        // Inače pusti formu da submit-uje na /search (default)
        break;

      case 'Escape':
        e.preventDefault();
        hideDropdown();
        searchInput.value = '';
        lastQuery = '';
        break;
    }
  }

  function moveActive(items, direction) {
    if (!items || items.length === 0) return;

    // Skini active sa trenutnog
    if (activeIndex >= 0 && items[activeIndex]) {
      items[activeIndex].classList.remove('is-active');
      items[activeIndex].setAttribute('aria-selected', 'false');
    }

    activeIndex += direction;
    if (activeIndex < 0) activeIndex = items.length - 1;
    if (activeIndex >= items.length) activeIndex = 0;

    items[activeIndex].classList.add('is-active');
    items[activeIndex].setAttribute('aria-selected', 'true');

    // Scroll u view ako treba
    if (typeof items[activeIndex].scrollIntoView === 'function') {
      items[activeIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  // ============== FOCUS / OUTSIDE CLICK ==============
  function handleFocus() {
    // Re-fokus input-a → kancelirati pending auto-blur
    cancelKeyboardBlurTimer();
    
    const value = searchInput.value.trim();
    if (value.length >= MIN_QUERY_LENGTH && currentItems.length > 0) {
      showDropdown();
      showState('results');
    }
  }

  // ============== BACKDROP ==============
  /**
   * Backdrop koji se prikazuje kad je search otvoren.
   * Hvata sve klikove ispod search overlay-a i zatvara search.
   * Postavlja se preko JS-a (po BiH modal pattern-u — overlay logika u JS-u).
   */
  let backdropEl = null;

  function showBackdrop() {
    if (backdropEl) return;
    backdropEl = document.createElement('div');
    backdropEl.id = 'GlorySearchBackdrop';
    backdropEl.setAttribute('aria-hidden', 'true');
    backdropEl.style.cssText = [
      'position: fixed !important',
      'top: 0 !important',
      'left: 0 !important',
      'right: 0 !important',
      'bottom: 0 !important',
      'width: 100% !important',
      'height: 100% !important',
      'background: rgba(26, 23, 20, 0.35) !important',
      'z-index: 300 !important', // ispod predictive (301) i overlay (302)
      'opacity: 0',
      'transition: opacity 0.25s ease',
      'cursor: pointer',
      'display: block !important'
    ].join(';');

    backdropEl.addEventListener('click', handleBackdropClick);

    document.body.appendChild(backdropEl);

    requestAnimationFrame(function () {
      if (backdropEl) backdropEl.style.opacity = '1';
    });
  }

  function hideBackdrop() {
    if (!backdropEl) return;
    const ref = backdropEl;
    ref.style.opacity = '0';
    backdropEl = null;
    setTimeout(function () {
      if (ref && ref.parentNode) ref.parentNode.removeChild(ref);
    }, 250);
  }

  function handleBackdropClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (searchOverlay) {
      searchOverlay.classList.remove('is-open');
    }
    if (searchInput) searchInput.blur();
  }

  // ============== OBSERVE OVERLAY STATE ==============
  /**
   * Kad se glory-search-overlay otvori → fokusiraj input + reposition.
   * Kad se zatvori → sakrij dropdown.
   */
  function observeOverlayState() {
    if (!searchOverlay || typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName === 'class') {
          if (!searchOverlay.classList.contains('is-open')) {
            hideDropdown();
            hideBackdrop();
            unlockBodyScroll();
            cancelKeyboardBlurTimer();
            // Reset stanja
            if (searchInput) {
              searchInput.value = '';
            }
            lastQuery = '';
            currentItems = [];
          } else {
            // Overlay se otvorio → lock scroll, prikaži backdrop, reposition
            lockBodyScroll();
            showBackdrop();
            setTimeout(positionDropdown, 50);
          }
        }
      });
    });

    observer.observe(searchOverlay, { attributes: true, attributeFilter: ['class'] });
  }

  // ============== BODY SCROLL LOCK ==============
  /**
   * Zaključa body scroll dok je search aktivan.
   * Pamti scrollY pre lock-a i vraća ga posle unlock-a (da ne skoči stranica).
   */
  let savedScrollY = 0;

  function lockBodyScroll() {
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + savedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }

  function unlockBodyScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    if (savedScrollY) {
      window.scrollTo(0, savedScrollY);
      savedScrollY = 0;
    }
  }

  // ============== HELPERS ==============

  /**
   * Highlight delove naziva koji se podudaraju sa query-jem.
   * Case-insensitive, multi-word safe.
   *
   * Radi nad plain tekstom i gradi HTML segment po segment,
   * tako da regex NIKAD ne udari u već ubačene <span> tagove
   * (npr. kad query sadrži slovo koje je deo class="..." atributa).
   */
  function highlightMatch(text, query) {
    if (!text || !query) return escapeHtml(text || '');

    const words = query.split(/\s+/).filter(function (w) { return w.length > 1; });
    if (words.length === 0) return escapeHtml(text);

    // Sort po dužini desc — duže reči prve da ne razbijemo highlight
    words.sort(function (a, b) { return b.length - a.length; });

    // Sastavi jedan combined regex sa svim rečima, sa zagradom za capture
    const pattern = words.map(escapeRegex).join('|');
    const regex = new RegExp('(' + pattern + ')', 'gi');

    // Split text-a po regex-u → niz parova [non-match, match, non-match, match, ...]
    // Svaki segment escape-ujemo posebno, matched segmenti se obmotaju spanom.
    const parts = text.split(regex);
    let result = '';
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!part) continue;
      // Parni indeksi (0, 2, 4...) su non-match, neparni (1, 3, 5...) su match
      if (i % 2 === 1) {
        result += '<span class="glory-predictive__highlight">' + escapeHtml(part) + '</span>';
      } else {
        result += escapeHtml(part);
      }
    }
    return result;
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Format cene iz Shopify suggest.json output-a.
   * Cena dolazi kao decimalni string u glavnoj jedinici, npr. "6000.00" = 6.000 RSD
   */
  function formatMoney(price, priceMin) {
    const value = price || priceMin || 0;
    const numeric = parseFloat(value);
    if (!numeric || isNaN(numeric)) return '';

    // Format: 6.000 RSD (sr-RS locale)
    const formatted = numeric.toLocaleString('sr-RS', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return formatted + ' RSD';
  }

  /**
   * Dodaj _240x size u Shopify CDN URL ako ga nema (za bolje thumbnaile).
   */
  function appendImageSize(url, size) {
    if (!url || typeof url !== 'string') return url;
    if (url.indexOf('_' + size + 'x') !== -1) return url;
    // Match na ekstenziju
    return url.replace(/\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i, '_' + size + 'x.$1$2');
  }

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }
})();